import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminOrderDetail, updateAdminOrderStatus } from "@/api/adminOrderApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"

const formatPrice = (value: number) => `${value?.toLocaleString("ko-KR") || 0}원`

const getPaymentText = (method: string) => {
    const map: Record<string, string> = { CARD: "카드결제", BANK: "무통장입금", KAKAOPAY: "카카오페이", OFFLINE: "수기결제(오프라인)" }
    return map[method] || method
}

// 💡 추가된 헬퍼 함수: 메시지 타입 변환
const getMessageTypeText = (type: string) => {
    const map: Record<string, string> = { none: "선택 안 함 (없음)", ribbon: "리본", card: "카드", ribbon_card: "리본 + 카드" }
    return map[type] || type || "없음"
}

// 💡 추가된 헬퍼 함수: 배송 타입 변환
const getDeliveryTypeText = (type: string) => {
    const map: Record<string, string> = { immediate: "즉시배송", specified: "시간지정" }
    return map[type] || type || "-"
}

export default function AdminOrderDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams()

    const [order, setOrder] = useState<any>(null)
    const [items, setItems] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [updating, setUpdating] = useState(false)
    const [status, setStatus] = useState("")

    useEffect(() => {
        const fetchDetail = async () => {
            if (!id) return;
            try {
                setLoading(true)
                const response = await getAdminOrderDetail(id)
                const data = response.data || response

                if (data.order) {
                    setOrder(data.order)
                    setStatus(data.order.orderStatus)
                }
                if (data.items) setItems(data.items)
            } catch (error) {
                console.error(error)
                alert("주문 상세 정보를 불러오는데 실패했습니다.")
                navigate("/admin/orders")
            } finally {
                setLoading(false)
            }
        }
        fetchDetail()
    }, [id, navigate])

    const handleStatusUpdate = async () => {
        if (!confirm("주문 상태를 변경하시겠습니까?")) return;
        try {
            setUpdating(true)
            await updateAdminOrderStatus(id!, status)
            alert("주문 상태가 변경되었습니다.")
            // 성공 후 최신 정보 반영
            setOrder((prev: any) => ({ ...prev, orderStatus: status }))
        } catch (error) {
            console.error(error)
            alert("상태 변경에 실패했습니다.")
        } finally {
            setUpdating(false)
        }
    }

    if (loading || !order) {
        return (
          <AdminLayout>
              <div className="flex h-[50vh] items-center justify-center">
                  <p className="text-slate-500">주문 정보를 불러오는 중...</p>
              </div>
          </AdminLayout>
        )
    }

    return (
      <AdminLayout>
          <div className="space-y-6">
              <div className="flex items-center justify-between">
                  <div>
                      <h2 className="text-3xl font-bold">주문 상세 정보</h2>
                      <p className="mt-2 text-sm text-slate-500">주문 내역과 배송지를 확인하고 상태를 변경합니다.</p>
                  </div>
                  <Button variant="outline" className="rounded-xl" onClick={() => navigate("/admin/orders")}>
                      목록으로
                  </Button>
              </div>

              <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
                  {/* 좌측: 주문 상품 및 배송 정보 */}
                  <div className="space-y-6">

                      {/* 1. 주문 상품 목록 */}
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardHeader className="flex flex-row items-center justify-between">
                              <CardTitle className="text-xl">주문 상품 ({items.length}건)</CardTitle>
                          </CardHeader>
                          <CardContent>
                              <div className="space-y-4">
                                  {items.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between rounded-xl border border-slate-100 p-4 bg-slate-50">
                                        <div>
                                            <p className="font-semibold text-slate-900">{item.productNameSnapshot}</p>
                                            <p className="text-sm text-slate-500 mt-1">
                                                {formatPrice(item.productPriceSnapshot)} / {item.quantity}개
                                            </p>
                                        </div>
                                        <p className="font-bold text-slate-900">
                                            {formatPrice(item.productPriceSnapshot * item.quantity)}
                                        </p>
                                    </div>
                                  ))}
                              </div>
                          </CardContent>
                      </Card>

                      {/* 2. 주문자 & 배송지 정보 */}
                      <div className="grid gap-6 md:grid-cols-2">
                          <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                              <CardHeader>
                                  <CardTitle className="text-lg">주문자 정보</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                  <div>
                                      <Label className="text-slate-500">주문자명</Label>
                                      <p className="font-medium mt-1">{order.ordererName}</p>
                                  </div>
                                  <div>
                                      <Label className="text-slate-500">연락처</Label>
                                      {/* 💡 주문자 연락처 클릭 시 SMS 전송 (하이픈 제거 후 sms: 링크 연결) */}
                                      <p className="font-medium mt-1">
                                          <a
                                            href={`sms:${order.ordererPhone?.replace(/-/g, "")}`}
                                            className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
                                            title="클릭하여 문자 보내기"
                                          >
                                              {order.ordererPhone}
                                          </a>
                                      </p>
                                  </div>
                              </CardContent>
                          </Card>

                          <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                              <CardHeader>
                                  <CardTitle className="text-lg">수령인 배송지 정보</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                  <div>
                                      <Label className="text-slate-500">수령인</Label>
                                      <p className="font-medium mt-1">
                                          {order.receiverName} (
                                          {/* 💡 수령인 연락처 클릭 시 SMS 전송 */}
                                          <a
                                            href={`sms:${order.receiverPhone?.replace(/-/g, "")}`}
                                            className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
                                            title="클릭하여 문자 보내기"
                                          >
                                              {order.receiverPhone}
                                          </a>
                                          )
                                      </p>
                                  </div>
                                  <div>
                                      <Label className="text-slate-500">주소</Label>
                                      <p className="font-medium mt-1">[{order.zipcode}] {order.address1} {order.address2}</p>
                                  </div>
                                  {order.deliveryMemo && (
                                    <div><Label className="text-slate-500">배송 메모</Label><p className="font-medium mt-1 text-rose-600">{order.deliveryMemo}</p></div>
                                  )}
                              </CardContent>
                          </Card>
                      </div>

                      {/* 3. 배송 일정 & 메시지 정보 */}
                      <div className="grid gap-6 md:grid-cols-2">
                          {/* 배송 및 행사 일정 */}
                          <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                              <CardHeader>
                                  <CardTitle className="text-lg flex items-center gap-2">
                                      배송 및 행사 일정
                                  </CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-5">
                                  <div>
                                      <Label className="text-slate-500">배송 요청일</Label>
                                      <p className="font-bold text-slate-900 mt-1">{order.deliveryDate || "-"}</p>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                      <div>
                                          <Label className="text-slate-500">배송 타입</Label>
                                          <p className="font-medium mt-1 text-indigo-600">
                                              {getDeliveryTypeText(order.deliveryType)}
                                              {order.deliveryType === "specified" && order.deliveryTime ? ` (${order.deliveryTime})` : ""}
                                          </p>
                                      </div>
                                      <div>
                                          <Label className="text-slate-500">행사 시간</Label>
                                          <p className="font-medium mt-1">{order.eventTime || "-"}</p>
                                      </div>
                                  </div>
                              </CardContent>
                          </Card>

                          {/* 메시지 (리본/카드) 정보 */}
                          <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                              <CardHeader>
                                  <CardTitle className="text-lg">메시지 정보</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                  <div>
                                      <Label className="text-slate-500">선택 옵션</Label>
                                      <p className="font-bold mt-1 text-emerald-600">
                                          {getMessageTypeText(order.messageType)}
                                      </p>
                                  </div>

                                  {/* 리본 정보 노출 */}
                                  {["ribbon", "ribbon_card"].includes(order.messageType) && (
                                    <div className="rounded-xl bg-emerald-50 p-4 border border-emerald-100 space-y-3">
                                        <div>
                                            <Label className="text-emerald-700 text-xs font-bold">경조사어 (리본)</Label>
                                            <p className="font-medium text-slate-900 mt-1">{order.ribbonPhrase || "-"}</p>
                                        </div>
                                        <div>
                                            <Label className="text-emerald-700 text-xs font-bold">보내는분 (리본)</Label>
                                            <p className="font-medium text-slate-900 mt-1">{order.ribbonSender || "-"}</p>
                                        </div>
                                    </div>
                                  )}

                                  {/* 카드 정보 노출 */}
                                  {["card", "ribbon_card"].includes(order.messageType) && (
                                    <div className="rounded-xl bg-emerald-50 p-4 border border-emerald-100 space-y-2">
                                        <Label className="text-emerald-700 text-xs font-bold">카드 내용</Label>
                                        <p className="font-medium text-slate-900 mt-1 whitespace-pre-wrap">{order.cardContent || "-"}</p>
                                    </div>
                                  )}
                              </CardContent>
                          </Card>
                      </div>
                  </div>

                  {/* 우측: 결제 정보 및 상태 변경 */}
                  <div className="space-y-6">
                      <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                          <CardHeader>
                              <CardTitle className="text-xl">결제 정보</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                              <div className="flex justify-between text-sm">
                                  <span className="text-slate-500">상품 금액</span>
                                  <span className="font-medium">{formatPrice(order.productAmount)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                  <span className="text-slate-500">배송비</span>
                                  <span className="font-medium">{formatPrice(order.shippingAmount)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                  <span className="text-slate-500">할인 금액</span>
                                  <span className="font-medium text-rose-500">-{formatPrice(order.discountAmount)}</span>
                              </div>
                              <hr className="border-slate-100" />
                              <div className="flex justify-between items-center pt-2">
                                  <span className="font-bold text-slate-900">총 결제 금액</span>
                                  <span className="text-2xl font-bold text-slate-900">{formatPrice(order.totalAmount)}</span>
                              </div>
                              <div className="mt-4 rounded-xl bg-slate-50 p-4 space-y-2 text-sm">
                                  <div className="flex justify-between"><span className="text-slate-500">결제 수단</span><span className="font-medium">{getPaymentText(order.paymentMethod)}</span></div>
                                  <div className="flex justify-between"><span className="text-slate-500">결제 일시</span><span className="font-medium">{order.paidAt || "-"}</span></div>
                              </div>
                          </CardContent>
                      </Card>

                      <Card className="rounded-[28px] border border-slate-200 shadow-sm border-slate-900 bg-slate-900 text-white">
                          <CardHeader>
                              <CardTitle className="text-xl text-white">주문 상태 관리</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                              <div className="space-y-2">
                                  <Label className="text-slate-300">현재 상태 변경</Label>
                                  <div className="flex gap-2">
                                      <select
                                        className="h-12 flex-1 rounded-xl bg-slate-800 px-3 text-sm text-white outline-none border border-slate-700 focus:border-slate-500"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                      >
                                          <option value="CREATED">주문생성</option>
                                          <option value="PAID">결제완료</option>
                                          <option value="PREPARING">배송준비중</option>
                                          <option value="SHIPPING">배송중</option>
                                          <option value="DONE">배송완료</option>
                                          <option value="CANCELED">주문취소</option>
                                      </select>
                                      <Button
                                        className="h-12 rounded-xl bg-white text-slate-900 hover:bg-slate-200"
                                        onClick={handleStatusUpdate}
                                        disabled={updating || status === order.orderStatus}
                                      >
                                          적용
                                      </Button>
                                  </div>
                              </div>
                              <p className="text-xs text-slate-400">송장 번호 입력 등의 추가 연동은 배송중 상태에서 진행됩니다.</p>
                          </CardContent>
                      </Card>
                  </div>
              </div>
          </div>
      </AdminLayout>
    )
}